import {CAMPAIGN_ID, getAccessToken, SIMLY_PARTNER_API_URL} from "../utils/config";
import {BasicResponse} from "./responses/basic.response";
import {ISearchCountryResponse} from "./responses/country.responses";
import {
    CampaignConversionGraphEntryDTO,
    CampaignGraphEntryDTO,
    CountryAnalyticDTO, CountryTopupAnalyticDTO,
    CountryUsageAnalyticDTO, DynamicLinkAnalyticsDTO, MonthlyAnalyticDTO,
} from "../dto/AnalyticDTO";
import DynamicLinkGraph from "../components/pages/analytics/graph/DynamicLinkGraph";

export class AnalyticsService {
    public static getCountryAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<CountryAnalyticDTO[]>> => {
        const dateParams = startDate && endDate ? `startDate=${startDate}&endDate=${endDate}` : '';
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/campaigns/${CAMPAIGN_ID}/country?${dateParams}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<CountryAnalyticDTO[]>;
    }

    public static getCountryUsageAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<CountryUsageAnalyticDTO[]>> => {
        const dateParams = startDate && endDate ? `startDate=${startDate}&endDate=${endDate}` : '';
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/campaigns/${CAMPAIGN_ID}/country/usage?${dateParams}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<CountryUsageAnalyticDTO[]>;
    }

    public static getCountryTopupAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<CountryTopupAnalyticDTO[]>> => {
        const dateParams = startDate && endDate ? `startDate=${startDate}&endDate=${endDate}` : '';
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/campaigns/${CAMPAIGN_ID}/country/topups?${dateParams}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<CountryTopupAnalyticDTO[]>;
    }

    public static getMonthlyAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<MonthlyAnalyticDTO[]>> => {
        const dateParams = startDate && endDate ? `startDate=${startDate}&endDate=${endDate}` : '';
        const resp = await fetch( `${SIMLY_PARTNER_API_URL}/dashboard/analytics/month?${dateParams}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<MonthlyAnalyticDTO[]>;
    }

    public static getCampaignGraphAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<CampaignGraphEntryDTO[]>> => {
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/campaigns/${CAMPAIGN_ID}/graphs/esim?startDate=${startDate || ''}&endDate=${endDate || ''}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<CampaignGraphEntryDTO[]>;
    }

    public static getCampaignConversionRatesAnalytics = async (startDate?: string, endDate?: string): Promise<BasicResponse<CampaignConversionGraphEntryDTO[]>> => {
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/campaigns/${CAMPAIGN_ID}/graphs/conversion?startDate=${startDate || ''}&endDate=${endDate || ''}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<CampaignConversionGraphEntryDTO[]>;
    }

    public static getDynamicLinkAnalytics = async (link: string, durationDays?: number): Promise<BasicResponse<DynamicLinkAnalyticsDTO>> => {
        const resp = await fetch(`${SIMLY_PARTNER_API_URL}/link/stats?dynamicLink=${link}&durationDays=${durationDays}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
                "x-simly-token": getAccessToken()!
            }
        });

        return await resp.json() as BasicResponse<DynamicLinkAnalyticsDTO>;
    }

    /**
     * Export activated eSIMs for a campaign within a date range
     * This triggers a file download in the browser
     *
     * @param campaignId - The ID of the campaign, defaults to global CAMPAIGN_ID if not provided
     * @param startDate - Start date in YYYY-MM-DD format
     * @param endDate - End date in YYYY-MM-DD format
     * @returns Promise that resolves when the download is initiated
     */
    public static exportActivatedSims = async (
        campaignId: string = CAMPAIGN_ID,
        startDate: string,
        endDate: string
    ): Promise<Blob> => {
        const url = `${SIMLY_PARTNER_API_URL}/campaigns/${campaignId}/activated-sims/export?startDate=${startDate}&endDate=${endDate}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "x-simly-token": getAccessToken()!
            }
        });

        if (!response.ok) {
            throw new Error(`Export failed with status: ${response.status}`);
        }

        return await response.blob();
    }
}
